import { Component, AfterViewInit, OnDestroy } from '@angular/core';
import {
  Auth,
  createUserWithEmailAndPassword,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  signInWithPopup,
  GoogleAuthProvider,
} from '@angular/fire/auth';
import {
  doc,
  Firestore,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  limit,
} from '@angular/fire/firestore';
import { NgForm } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { MatDialog } from '@angular/material/dialog';
import { GeneralTermsComponent } from 'src/app/components/general-terms/general-terms.component';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
} from '@angular/fire/storage';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements AfterViewInit, OnDestroy {
  // ... existing properties ...

  ngOnDestroy() {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
    }
  }

  // ... existing methods ...
  step: number = 1;
  signupMethod: 'email' | 'mobile' = 'mobile';
  isSubmitting = false;
  submitted = false;
  selectedFile: File | null = null;
  confirmPassword: string = '';
  hidePassword: boolean = true;
  hideConfirmPassword: boolean = true;

  otp: string = '';
  confirmationResult: ConfirmationResult | null = null;
  otpSent = false;
  otpVerified = false;
  recaptchaVerifier!: RecaptchaVerifier;

  otpDigits: string[] = ['', '', '', '', '', ''];
  countryList = [
    { name: 'UAE', code: '+971', flag: 'assets/flags/uae.png', limit: 9 },
    { name: 'India', code: '+91', flag: 'assets/flags/india.png', limit: 10 },
    {
      name: 'Saudi Arabia',
      code: '+966',
      flag: 'assets/flags/saudi.png',
      limit: 9,
    },
    { name: 'Kuwait', code: '+965', flag: 'assets/flags/kuwait.png', limit: 8 },
    { name: 'Oman', code: '+968', flag: 'assets/flags/oman.png', limit: 8 },
    { name: 'Qatar', code: '+974', flag: 'assets/flags/qatar.png', limit: 8 },
  ];

  /* Back Button Logic */
  handleBack() {
    if (this.step === 2) {
      this.step = 1;
      this.otpSent = false;
      this.otpVerified = false;
    } else if (this.otpSent && !this.otpVerified) {
      this.otpSent = false;
      this.otpDigits = ['', '', '', '', '', ''];
      this.otp = '';
    } else {
      // Navigate away or close? User is on first screen.
      this.router.navigate(['/login']); // Or login
    }
  }

  /* OTP Input Focus Logic */
  moveFocus(event: any, index: number) {
    const input = event.target;
    let val = input.value.replace(/[^0-9]/g, '');

    // Handle Paste / Autofill
    if (val.length > 1) {
      for (let i = 0; i < val.length && index + i < 6; i++) {
        this.otpDigits[index + i] = val[i];
      }
      this.otp = this.otpDigits.join('');

      const nextIndex = Math.min(index + val.length, 5);
      setTimeout(() => {
        const nextInput = document.getElementById(`otp-${nextIndex}`);
        nextInput?.focus();
      }, 0);
      return;
    }

    // Keep only the last character typed (prevents duplication)
    const singleVal = val.slice(-1);
    input.value = singleVal;
    this.otpDigits[index] = singleVal;

    if (singleVal && index < 5) {
      setTimeout(() => {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }, 0);
    }

    // Update main OTP string
    this.otp = this.otpDigits.join('');
  }

  switchSignupMethod(method: 'email' | 'mobile') {
    this.signupMethod = method;
    this.submitted = false;
  }

  trackByIdx(index: number): any {
    return index;
  }

  handleBackspace(event: any, index: number) {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (this.otpDigits[index]) {
        // Clear current field
        this.otpDigits[index] = '';
        event.target.value = '';
      } else if (index > 0) {
        // Move to previous field and clear it
        this.otpDigits[index - 1] = '';
        const prevInput = document.getElementById(
          `otp-${index - 1}`,
        ) as HTMLInputElement;
        if (prevInput) {
          prevInput.value = '';
          prevInput.focus();
        }
      }
      this.otp = this.otpDigits.join('');
    }
  }

  /* Apple Signup Placeholder */
  appleSignup() {
    this.uxservice.showError('Apple Sign-In is not yet configured.');
  }

  /* Helper for Template */
  getSelectedCountry() {
    return this.countryList.find((c) => c.code === this.user.countryCode);
  }

  get currentPhoneLimit(): number {
    const country = this.countryList.find(
      (c) => c.code === this.user.countryCode,
    );
    return country ? country.limit : 10;
  }

  // Allow only numbers
  numberOnly(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }
  user: any = {
    firstname: '',
    emailid: '',
    password: '',
    phoneNumber: '',
    countryCode: '+971',
    profilePicUrl: '',
  };

  termsAccepted = false;

  constructor(
    private auth: Auth,
    private afs: Firestore,
    private storage: Storage,
    private router: Router,
    private route: ActivatedRoute,
    private uxservice: UxserviceService,
    private dialog: MatDialog,
  ) { }

  ngAfterViewInit() {
    this.recaptchaVerifier = new RecaptchaVerifier(
      'register-recaptcha-container',
      { size: 'invisible' },
      this.auth,
    );
    this.recaptchaVerifier.render().catch(e => console.warn('Recaptcha render error', e));
  }

  // Step navigation — kept for backwards-compat but no longer used in the new single-step flow
  goToStep2() {
    if (this.signupMethod === 'mobile' && !this.otpVerified) {
      this.uxservice.showError('Please verify OTP before continuing');
      return;
    }
    this.step = 2;
  }

  goBack() {
    this.step = 1;
  }

  // Send OTP
  async sendOtp() {
    this.submitted = true;
    if (!this.user.phoneNumber) {
      this.uxservice.showError('Please enter a phone number');
      return;
    }

    // Initialize reCAPTCHA if not already done
    if (!this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier = new RecaptchaVerifier(
          'register-recaptcha-container',
          { size: 'invisible' },
          this.auth,
        );
      } catch (e: any) {
        console.warn('Recaptcha init error:', e);
        // If it failed because it was already rendered, we might need to assume it's there?
        // But the proper way is to just not init it if we have the reference.
        // If "already rendered" happens but we don't have reference, we are in trouble.
        // But here we check !this.recaptchaVerifier.
      }
    }

    try {
      const safePhone = this.user.phoneNumber.replace(/\D/g, '');
      const phoneNumber = `${this.user.countryCode}${safePhone}`;

      // Check if phone number is already registered in users-list
      const usersRef = collection(this.afs, 'users-list');
      const q = query(
        usersRef,
        where('phoneNumber', '==', phoneNumber),
        limit(1),
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        this.uxservice.showError(
          'Mobile number is already registered. Please login to enter your password.',
        );
        this.router.navigate(['/login']);
        return;
      }
      this.confirmationResult = await signInWithPhoneNumber(
        this.auth,
        phoneNumber,
        this.recaptchaVerifier,
      );
      this.otpSent = true;
      this.uxservice.showSuccess('OTP sent to your phone');
    } catch (error: any) {
      if (error.code === 'auth/too-many-requests') {
        this.uxservice.showError(
          'Too many OTP attempts. Please wait a while or use a different number.',
        );
      } else {
        this.uxservice.showError('Failed to send OTP: ' + error.message);
      }
      return;
    }
  }

  // Verify OTP
  async verifyOtp() {
    if (!this.confirmationResult) return;

    /* Combine digits if using array */
    this.otp = this.otpDigits.join('');

    if (!this.otp || this.otp.length !== 6) {
      this.uxservice.showError('Please enter the 6-digit OTP');
      return;
    }

    this.isSubmitting = true;
    this.uxservice.showOverlay('Verifying OTP...');

    try {
      const result = await this.confirmationResult.confirm(this.otp);
      this.otpVerified = true;
      this.user.uid = result.user.uid;
      this.uxservice.instantCloseOverlay();
      this.uxservice.showSuccess('OTP verified successfully');
      // Save user immediately and redirect — no step 2 anymore
      await this.saveCustomerData(result.user.uid, (this.user.emailid || '').toLowerCase());
    } catch (error: any) {
      console.error('OTP verification error:', error);
      this.uxservice.instantCloseOverlay();
      this.uxservice.showError('Invalid OTP. Try again.');
      this.otpVerified = false;
      this.isSubmitting = false;

      // Clear OTP and Focus first digit
      this.otpDigits = ['', '', '', '', '', ''];
      this.otp = '';
      setTimeout(() => {
        const firstInput = document.getElementById('otp-0');
        firstInput?.focus();
      }, 100);
    }
  }

  // onTermsCheckboxChange(event: any) {
  //   if (event.checked) {
  //     // Force uncheck visually while dialog is open or until accepted
  //     event.source.checked = this.termsAccepted;
  //     this.openTermsDialog();
  //   } else {
  //     this.termsAccepted = false;
  //   }
  // }
  onTermsCheckboxChange(event: any) {
    this.termsAccepted = event.checked;
  }

  // openTermsDialog(event?: Event) {
  //   if (event) {
  //     event.preventDefault();
  //     event.stopPropagation();
  //   }
  //   const dialogRef = this.dialog.open(GeneralTermsComponent, {
  //     width: '1200px',
  //     panelClass: 'custom-dialog-container',
  //     backdropClass: 'blurred-backdrop',
  //     disableClose: true
  //   });

  //   dialogRef.afterClosed().subscribe((result) => {
  //     if (result === true) {
  //       this.termsAccepted = true;
  //     } else {
  //       this.termsAccepted = false;
  //     }
  //   });
  // }
  openTermsDialog(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.dialog.open(GeneralTermsComponent, {
      width: '1200px',
      panelClass: 'custom-dialog-container',
      backdropClass: 'blurred-backdrop',
      disableClose: false,
    });
  }

  // File upload (profile picture)
  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];

      try {
        const filePath = `profilePics/${Date.now()}_${this.selectedFile.name}`;
        const storageRef = ref(this.storage, filePath);
        await uploadBytes(storageRef, this.selectedFile);

        const downloadURL = await getDownloadURL(storageRef);
        this.user.profilePicUrl = downloadURL;
        this.uxservice.showSuccess('Profile picture uploaded successfully');
      } catch (error: any) {
        console.error('File upload error:', error);
        this.uxservice.showError('File upload failed. Try again.');
      }
    }
  }

  /** ── Email Registration (called directly from Step 1 — no step 2 needed) ── */
  async registerWithEmail(form: NgForm) {
    // Mark all fields touched so validation errors show
    Object.keys(form.controls).forEach((k) =>
      form.controls[k]?.markAsTouched(),
    );

    if (form.invalid) {
      this.submitted = true;
      return;
    }

    if (!this.user.password || !this.confirmPassword) {
      this.uxservice.showError('Please fill in all password fields');
      return;
    }

    if (this.user.password !== this.confirmPassword) {
      this.uxservice.showError('Passwords do not match');
      return;
    }

    this.isSubmitting = true;
    this.uxservice.showOverlay('Creating Account...');

    try {
      const email = this.user.emailid.toLowerCase();
      const credential = await createUserWithEmailAndPassword(
        this.auth,
        email,
        this.user.password,
      );
      await this.saveCustomerData(credential.user.uid, email);
    } catch (error: any) {
      this.isSubmitting = false;
      this.uxservice.instantCloseOverlay();
      if (error.code === 'auth/email-already-in-use') {
        this.uxservice.showError(
          'Email is already registered. Please login to enter your password.',
        );
        this.router.navigate(['/login']);
      } else {
        this.uxservice.showError(error.message);
      }
    }
  }

  // Form submission (legacy — kept but no longer referenced from HTML)
  submitHandler(form: NgForm, e: Event) {
    e.preventDefault();

    if (form.invalid) {
      Object.keys(form.controls).forEach((key) => {
        form.controls[key]?.markAsTouched();
      });
      return;
    }

    this.isSubmitting = true;
    this.uxservice.showOverlay('Creating Account...');

    if (this.signupMethod === 'email') {
      const email = this.user.emailid;
      const password = this.user.password;

      createUserWithEmailAndPassword(this.auth, email, password)
        .then(async (credential) => {
          await this.saveCustomerData(credential.user.uid, email);
        })
        .catch((error) => {
          this.isSubmitting = false;
          this.uxservice.instantCloseOverlay();
          if (error.code === 'auth/email-already-in-use') {
            this.uxservice.showError(
              'Email is already registered. Please login to enter your password.',
            );
            this.router.navigate(['/login']);
          } else {
            this.uxservice.showError(error.message);
          }
        });
    } else {
      if (!this.user.uid) {
        this.uxservice.instantCloseOverlay();
        this.uxservice.showError('Please verify phone number first');
        return;
      }
      this.saveCustomerData(this.user.uid, (this.user.emailid || '').toLowerCase());
    }
  }

  private async saveCustomerData(uid: string, email: string) {
    try {
      const userRef = doc(this.afs, 'users-list', uid);

      const phone = this.user.phoneNumber
        ? `${this.user.countryCode}${this.user.phoneNumber}`
        : '';

      const userData = {
        firstname: this.user.firstname || '',
        displayname: this.user.firstname || '',
        email: email.toLowerCase(),
        phoneNumber: phone,
        role: 'customer', // Default to customer role
        isIndividual: true, // New users are individual customers by default
        isVisiting: false, // No longer using visiting state
        createdAt: new Date().toISOString(),
        profilePicUrl: this.user.profilePicUrl || '',
      };

      await setDoc(userRef, userData, { merge: true });

      this.uxservice.instantCloseOverlay();
      // ✅ No success toast here — it will fire AFTER the user picks their role
      // on the landing page (checkRoleAndShowDialog → dialog close)

      const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';
      this.router.navigateByUrl(returnUrl);
    } catch (error: any) {
      console.error('Firestore save error:', error);
      this.uxservice.instantCloseOverlay();
      this.uxservice.showError(error.message);
      this.isSubmitting = false;
    }
  }

  async googleSignup() {
    this.isSubmitting = true;
    this.uxservice.showOverlay('Connecting to Google...');
    try {
      const provider = new GoogleAuthProvider();
      const credential = await signInWithPopup(this.auth, provider);
      const user = credential.user;

      // Check if user exists
      const userRef = doc(this.afs, 'users-list', user.uid);
      const docSnap = await getDoc(userRef);

      if (docSnap.exists()) {
        await this.auth.signOut();
        this.uxservice.instantCloseOverlay();
        this.uxservice.showError('Account already exists. Please login.');
        const returnUrl =
          this.route.snapshot.queryParams['returnUrl'] || '/home';
        this.router.navigate(['/login'], { queryParams: { returnUrl } });
      } else {
        // New User — register as normaluser, role selected on homepage
        let firstName = user.displayName
          ? user.displayName.split(' ')[0]
          : 'User';
        this.user.firstname =
          firstName.charAt(0).toUpperCase() + firstName.slice(1);
        this.user.profilePicUrl = user.photoURL || '';

        await this.saveCustomerData(user.uid, (user.email || '').toLowerCase());
      }
    } catch (error: any) {
      this.uxservice.instantCloseOverlay();
      this.isSubmitting = false;
    }
  }
}
